import { useState, useEffect, useCallback } from 'react';
import { useSearchVehicleOrders } from '../../../../api/services/lupp/order/importer/importer';

type UseVehicleOrdersParams = {
  importerOrderId: string | null;
  enabled?: boolean;
};

const WAITING_FOR_CONFIRMATION = 'WAITING FOR CONFIRMATION';

/**
 * Maps filter card display values to the API status filter arrays.
 * Multi-status cards (e.g. 'DENIED / CANCELED') map to multiple API statuses.
 */
const CARD_VALUE_TO_API_STATUSES: Record<string, string[]> = {
  'IN REVIEW': ['IN REVIEW'],
  'WAITING FOR CONFIRMATION': ['WAITING FOR CONFIRMATION'],
  CONFIRMED: ['CONFIRMED'],
  'READY FOR DISPATCH': ['READY FOR DISPATCH'],
  FULFILLED: ['FULFILLED'],
  'DENIED / CANCELED': ['REJECTED', 'CANCELLED'],
};

export const useVehicleOrders = ({ importerOrderId, enabled = true }: UseVehicleOrdersParams) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string[] | undefined>();

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setDebouncedSearch('');
    setStatusFilter(undefined);
    setSortField(undefined);
    setSortOrder('desc');
  }, [importerOrderId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const apiResult = useSearchVehicleOrders({
    importerOrderId,
    page: currentPage,
    size: pageSize,
    searchString: debouncedSearch || undefined,
    sortField,
    sortOrder,
    statusFilter,
    enabled: enabled && !!importerOrderId,
  });

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortField(column);
    setSortOrder(direction);
    setCurrentPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((value: string | null) => {
    setStatusFilter(value ? (CARD_VALUE_TO_API_STATUSES[value] ?? [value]) : undefined);
    setCurrentPage(0);
  }, []);

  const items = apiResult.data?.items ?? [];
  const pendingConfirmationOrders = items.filter((o) => o.statusName === WAITING_FOR_CONFIRMATION);

  return {
    data: items,
    totalCount: apiResult.data?.totalCount ?? 0,
    statusGroupCounts: apiResult.data?.statusGroupCounts ?? {},
    isLoading: apiResult.isLoading,
    isFetching: apiResult.isFetching,
    error: apiResult.error,
    currentPage,
    pageSize,
    searchQuery,
    debouncedSearch,
    sortField,
    sortOrder,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSearchChange: handleSearchChange,
    onSort: handleSort,
    onStatusFilterChange: handleStatusFilterChange,
    pendingConfirmationOrders,
  };
};
